import React, { useEffect } from "react"
import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material"
import Close from '@mui/icons-material/Close'
import { dateTimeFormat, elapsedTime } from "~/utils/dateTime"
import useSystemState from "~/features/System/stores/SystemState"
import { getTimeLineDispatch } from "~/features/Occurrence/services"
import { useHookstate } from "@hookstate/core"
import TimelineDispatchesOccurrence from "~/features/Occurrence/components/OccurrenceList/components/timeLineDispatchesOccurrence"
import { useLang } from "~/hooks/useLang"
import type { TimeLineOccurrenceDispatchProps } from "~/features/Occurrence/interfaces/IDispatchOccurrence"
import { useMarkerState } from "~/features/Map/stores/MapState"
import useDispatchState from "~/features/Dispatch/stores/DispatchState"
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';


// eslint-disable-next-line max-lines-per-function
export default function MarkerPopupContentDispatchHeatMap({
  id,
  status,
  code,
  typeSubtype,
  location,
  dispatchedAt,
  closeThisPopUp,
  dispatchGroupObj,
  codeList,
  dispatches,
  statusObj,
  agencyObj,
  composeTypeSubtype,
}) {
  const { translate } = useLang()
  const { getStatusColors } = useSystemState()
  const { syncDispatchFromId } = useDispatchState();
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const open = useHookstate(false)
  const statusColors = getStatusColors(status)
  const timeLine = useHookstate<TimeLineOccurrenceDispatchProps[]>([])
  const dispatchOpenned = useHookstate<any | null>(null)
  const handleClickOpen = (dispatchId) => {
    timeLine.set([])
    getTimeLineDispatch({ dispatchId }).then(
      (response) => {
        if (response.status === 200) {
          timeLine.set(response.data)
        }
      }).finally(() => { 
        syncDispatchFromId(dispatchId)
        open.set(true)
      })
  }

  const handleClose = () => {
    open.set(false)
  }

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };


  return (
    <>
      <Card
        sx={{
          backgroundColor: 'transparent',
          backgroundImage: 'none',
          boxShadow: 'none',
        }}
      >
        <CardContent sx={{ padding: '1em 0 1em 0', textAlign: 'center' }}>
          {code ? (
            <>
              <Stack direction="row" alignItems="center" gap={1}>
                <Tooltip
                  title={`Despachado em: ${dispatchedAt ? dateTimeFormat(dispatchedAt?.toString()) : ''}`}
                  placement="top"
                >
                  <Chip
                    variant={'outlined'}
                    label={dispatchedAt ? elapsedTime(dispatchedAt?.toString()) : 'N/D'}
                  />
                </Tooltip>
                <Chip
                  onClick={() => handleClickOpen(id)}
                  sx={{
                    background: statusColors.main,
                    color: statusColors.contrastText,
                    '&:hover': {
                      background: statusColors.dark,
                    },
                  }}
                  label={
                    <Typography variant="subtitle1">
                      {code}
                    </Typography>
                  }
                />
                <Tooltip title={`${translate('Close')}`} placement="top">
                  <IconButton size="small" onClick={() => closeThisPopUp()}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip >
              </Stack>
              <Typography variant="subtitle1" sx={{ margin: '.6em 0' }}>
                {` ${typeSubtype}`}
              </Typography>
              <Typography variant="subtitle1" sx={{ margin: '.6em 0' }}>
                {` ${location}`}
              </Typography>
            </>
          ) : codeList.length ? (
            <>
              <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography variant="subtitle1" sx={{ margin: '.6em 0' }}>
                    {translate('Dispatches with the same position')}
                </Typography>
                <Tooltip title={`${translate('Close')}`} placement="top">
                  <IconButton size="small" onClick={() => closeThisPopUp()}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box width={'100%'}>
                <Stack maxHeight={'300px'}>
                  <Box display={'flex'} flexDirection={'column'} alignItems="flex-start" justifyContent={'space-between'} sx={{ overflowY: 'auto' }}>
                    {codeList.map((codeItem, index) => {
                      const findDispatch = dispatches.find(dispatch => dispatch.code === codeItem);
                      const typeSubtype = composeTypeSubtype(findDispatch.type.name, findDispatch.subtype ? findDispatch.subtype.name : null);
                      const statusColorsDispatch = getStatusColors(findDispatch.status.id);

                      return (
                        <Accordion
                          key={codeItem}
                          expanded={expanded === `panel${index + 1}`}
                          onChange={handleChange(`panel${index + 1}`)}
                          sx={{ width: '100%', background: 'transparent', boxShadow: 'none' }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1bh-content"
                            id="panel1bh-header"
                            sx={{
                              paddingX: 0,
                              '& .MuiAccordionSummary-content': {
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                paddingRight: 5,
                                gap: 1,
                              },
                            }}
                          >
                            <Tooltip
                              title={`Despachado em: ${findDispatch.dispatched_at ? dateTimeFormat(findDispatch.dispatched_at?.toString()) : ''}`}
                              placement="top"
                            >
                              <Chip
                                variant={'outlined'}
                                label={findDispatch.dispatched_at ? elapsedTime(findDispatch.dispatched_at?.toString()) : 'N/D'}
                              />
                            </Tooltip>

                            <Chip
                              sx={{
                                marginLeft: 1,
                                background: statusColorsDispatch.main,
                                color: statusColorsDispatch.contrastText,
                                '&:hover': {
                                  background: statusColorsDispatch.dark,
                                },
                              }}
                              label={
                                <Typography variant="subtitle1">
                                  {codeItem}
                                </Typography>
                              }
                            />

                            <Tooltip title={`${translate('See Chronology')}`} placement="top">
                              <IconButton
                                sx={{
                                  marginLeft: 1,
                                  paddingTop: 0.5,
                                }}
                                aria-label="chronology"
                                onClick={((event) => {
                                  event.stopPropagation();
                                  handleClickOpen(findDispatch.id)
                                  dispatchOpenned.set(JSON.parse(JSON.stringify(findDispatch)));
                                })}
                              >
                                <AccessTimeFilledIcon />
                              </IconButton>
                            </Tooltip>
                          </AccordionSummary>
                          <AccordionDetails>
                            <>
                              <Typography variant="subtitle1">
                                {` ${typeSubtype}`}
                              </Typography>
                              <Typography variant="subtitle1">
                                {` ${findDispatch.location}`}
                              </Typography>
                            </>
                          </AccordionDetails>
                        </Accordion>
                      )
                    })}
                  </Box>
                </Stack>
              </Box>
            </>
          ) :
            false}
        </CardContent>
      </Card>
      <Dialog
        maxWidth={'sm'}
        fullWidth={true}
        open={open.get()}
        onClose={handleClose}
        sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column'}}
      >
        <DialogTitle>
          {`${translate('Dispatch')} - ${code ? code : dispatchOpenned.get()?.code} - ${code ? statusObj?.acronym : dispatchOpenned.get()?.status?.acronym}`}
          <Typography variant="subtitle2">
            {`${code ? dispatchGroupObj?.name : dispatchOpenned.get()?.dispatch_group?.name} - ${code ? agencyObj?.name : dispatchOpenned.get()?.agency?.name}`}
          </Typography>
          <Typography variant="subtitle2">
            {code ? typeSubtype : composeTypeSubtype(dispatchOpenned.get()?.type?.name, dispatchOpenned.get()?.subtype ? dispatchOpenned.get()?.subtype?.name : null)}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TimelineDispatchesOccurrence timeLinePoint={timeLine} />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleClose}>
            {translate('Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
